import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import WorkshopQuoteSheet, { WorkshopPhotoReport } from "@/components/WorkshopQuoteSheet";
import SignaturePad from "@/components/SignaturePad";
import { PenLine, CheckCircle2, FileText, Printer, Images } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicWorkshopQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [showPad, setShowPad] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [doc, setDoc] = useState("cotizacion"); // cotizacion | fotos

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/public/workshop/client/${token}`);
      setData(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Enlace no válido");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const authorize = async (dataUrl) => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/public/workshop/client/${token}/authorize`, { name: name.trim(), signature: dataUrl });
      setShowPad(false);
      setMsg("✓ Cotización autorizada correctamente. ¡Gracias!");
      load();
    } catch (err) {
      setMsg(err.response?.data?.detail || "Error al autorizar. Intenta de nuevo.");
      setShowPad(false);
    } finally { setSaving(false); }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 text-center" style={{ background: "#0a0f1d", color: "#fca5a5" }}>
      <div><FileText className="w-10 h-10 mx-auto mb-3 opacity-60" /><div className="font-semibold">{error}</div></div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0f1d", color: "#94a3b8" }}>Cargando…</div>;

  const { quote, settings } = data;
  const photoUrl = (fid) => `${API}/api/public/workshop/client/${token}/file/${fid}`;
  const pending = quote.status === "borrador";

  return (
    <div className="min-h-screen pb-10" style={{ background: "#1e293b" }} data-testid="public-client-quote-page">
      <style>{`@media print { .no-print { display: none !important; } body { background: white !important; } }`}</style>

      <div className="no-print px-4 py-3 text-center text-xs uppercase tracking-widest font-semibold text-slate-300" style={{ background: "#0a0f1d", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        Cotización {quote.folio} — {settings?.name}
      </div>

      {msg && (
        <div className="no-print max-w-[820px] mx-auto mt-4 px-4">
          <div className="px-4 py-3 rounded text-sm font-semibold" data-testid="client-auth-msg"
            style={{ background: msg.startsWith("✓") ? "rgba(34,197,94,0.15)" : "rgba(220,38,38,0.15)", border: `1px solid ${msg.startsWith("✓") ? "#16a34a" : "#dc2626"}`, color: msg.startsWith("✓") ? "#86efac" : "#fca5a5" }}>
            {msg}
          </div>
        </div>
      )}

      {pending && (
        <div className="no-print max-w-[820px] mx-auto mt-4 px-4">
          <div className="rounded p-4 space-y-3" style={{ background: "rgba(34,211,238,0.08)", border: "1px solid #22d3ee" }}>
            <div className="text-sm font-semibold" style={{ color: "#a5f3fc" }}>
              Revise la cotización y, si está de acuerdo, autorícela con su nombre y firma:
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                data-testid="client-auth-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre completo de quien autoriza"
                className="flex-1 px-3 py-2.5 rounded text-sm"
                style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.2)", color: "white" }}
              />
              <button
                data-testid="client-auth-sign-btn"
                disabled={!name.trim()}
                onClick={() => setShowPad(true)}
                className="px-5 py-2.5 rounded font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-40"
                style={{ background: "#16a34a", color: "white" }}
              >
                <PenLine className="w-4 h-4" /> Firmar y autorizar
              </button>
            </div>
          </div>
        </div>
      )}

      {quote.status === "aprobada" && quote.client_authorized_name && !msg && (
        <div className="no-print max-w-[820px] mx-auto mt-4 px-4">
          <div className="px-4 py-3 rounded text-sm flex items-center gap-2" style={{ background: "rgba(34,197,94,0.15)", border: "1px solid #16a34a", color: "#86efac" }}>
            <CheckCircle2 className="w-4 h-4" /> Autorizada por {quote.client_authorized_name} el {new Date(quote.client_authorized_at).toLocaleString("es-MX")}
          </div>
        </div>
      )}

      <div className="no-print max-w-[820px] mx-auto mt-3 px-4 flex items-center justify-end gap-2 flex-wrap">
        {(quote.photos || []).length > 0 && (
          <div className="flex rounded overflow-hidden" data-testid="public-doc-toggle" style={{ border: "1px solid #f59e0b" }}>
            {[["cotizacion", "Cotización", FileText], ["fotos", "Reporte fotográfico", Images]].map(([d, l, Icon]) => (
              <button key={d} data-testid={`public-doc-${d}`} onClick={() => setDoc(d)}
                className="px-3 py-1.5 text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5"
                style={d === doc ? { background: "#f59e0b", color: "#1c1006" } : { background: "transparent", color: "#f59e0b" }}>
                <Icon className="w-3.5 h-3.5" /> {l}
              </button>
            ))}
          </div>
        )}
        <button onClick={() => window.print()} className="px-3 py-1.5 rounded text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5" style={{ background: "white", color: "#0c1424" }}>
          <Printer className="w-3.5 h-3.5" /> Imprimir / PDF
        </button>
      </div>

      <div className="py-4 px-2">
        {doc === "fotos"
          ? <WorkshopPhotoReport quote={quote} settings={settings} photoUrl={photoUrl} />
          : <WorkshopQuoteSheet quote={quote} settings={settings} photoUrl={photoUrl} hideStatus />}
      </div>

      {showPad && (
        <SignaturePad
          title={`Firma de ${name.trim()}`}
          saving={saving}
          onClose={() => setShowPad(false)}
          onConfirm={(dataUrl) => authorize(dataUrl)}
        />
      )}
    </div>
  );
}
